import React, { FC, useState } from 'react';
import { FaChevronRight } from 'react-icons/fa';
import styled, { css, keyframes } from 'styled-components';

interface Props {
	hsCode: string;
	title?: string;
	onClick?: (e: React.MouseEvent<HTMLElement>) => void;
	disabled?: boolean;
}

/**
 * A selectable HS code search result: the code and its description, with an arrow.
 */
export const HsCodeCard: FC<Props> = ({ hsCode, title, onClick, disabled }) => {
	const clickable = !!onClick && !disabled;

	const handleClick = (e: React.MouseEvent<HTMLElement>) => {
		if (clickable) onClick(e);
	};

	// Sentence-case the (often upper-cased) description coming from the API.
	const prettyTitle = title ? title.charAt(0) + title.slice(1).toLowerCase() : '';

	return (
		<StyledCard
			$clickable={clickable}
			$disabled={disabled}
			tabIndex={disabled ? -1 : 0}
			role={onClick ? 'button' : undefined}
			aria-disabled={disabled || undefined}
			onClick={handleClick}
			onKeyDown={e => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					handleClick(e as unknown as React.MouseEvent<HTMLElement>);
				}
			}}>
			<Title>
				<Code>{hsCode}</Code>
				{title && ` - ${prettyTitle}`}
			</Title>
			{clickable && <StyledArrow />}
		</StyledCard>
	);
};

const shimmer = keyframes`
	0% { background-position: 200% 0; }
	100% { background-position: -200% 0; }
`;

export const HsCodeCardSkeleton: FC = () => (
	<StyledCard $isSkeleton>
		<SkeletonLine widthMin={60} widthMax={90} />
	</StyledCard>
);

const StyledCard = styled.div<{
	$isSkeleton?: boolean;
	$clickable?: boolean;
	$disabled?: boolean;
}>`
	position: relative;
	display: grid;
	align-content: center;
	padding: 1.5rem 4rem 1.5rem 1.5rem;
	border: 2px solid ${({ theme }) => theme.colors.borderColor};
	border-radius: 0.5rem;
	cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
	transition: border-color 150ms;
	min-width: 0;
	word-break: break-word;

	${({ $isSkeleton, $clickable, theme }) =>
		!$isSkeleton &&
		$clickable &&
		css`
			&:hover,
			&:focus-within,
			&:focus {
				border-color: ${theme.colorsV2.mainPink};
				outline: none;
			}
		`}

	${({ $disabled }) =>
		$disabled &&
		css`
			cursor: not-allowed;
			opacity: 0.45;
		`}
`;

interface SkeletonLineProps {
	// Width as a percentage, picked randomly between widthMin and widthMax
	widthMin: number;
	widthMax: number;
}

const SkeletonLine: FC<SkeletonLineProps> = ({ widthMin, widthMax }) => {
	// Picked once per mount so the width doesn't change on re-render
	const [width] = useState(() => `${widthMin + Math.random() * (widthMax - widthMin)}%`);
	return <SkeletonBar $width={width} />;
};

const SkeletonBar = styled.div<{ $width: string }>`
	height: 2.4rem;
	width: ${({ $width }) => $width};
	border-radius: 6px;
	background: linear-gradient(
		90deg,
		${({ theme }) => theme.colors.grayLight} 25%,
		${({ theme }) => theme.colors.grayLighter} 50%,
		${({ theme }) => theme.colors.grayLight} 75%
	);
	background-size: 200% 100%;
	animation: ${shimmer} 2.3s infinite;
`;

const Title = styled.p`
	margin: 0;
	font-size: 1.75rem;
	color: ${({ theme }) => theme.colors.black};
`;

const Code = styled.b`
	font-family: ${({ theme }) => theme.fonts.ArimoBold};
`;

const StyledArrow = styled(FaChevronRight)`
	position: absolute;
	right: 1.5rem;
	top: 50%;
	transform: translateY(-50%);
	color: ${({ theme }) => theme.colorsV2.mainPink};
`;
